import { getGlobalObject } from './host-environment';

export function safeParse<T>(value: string | undefined | null, fallback: T): T {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function memoize<T, P extends any[] = any[]>(
  fn: (...args: P) => T,
  compare: (a: P, b: P) => boolean = arraysEqual,
) {
  let call: [P, T] | undefined;

  return (...newArgs: P): T => {
    if (!call || !compare(call[0], newArgs)) {
      call = [newArgs, fn(...newArgs)];
    }
    return call[1];
  };
}

function arraysEqual(xs: any[], ys: any[]) {
  if (xs.length !== ys.length) {
    return false;
  }
  for (let i = 0; i < xs.length; i++) {
    if (xs[i] !== ys[i]) {
      return false;
    }
  }
  return true;
}

export function swapArrayElements<T extends unknown[]>(array: T, indexA: number, indexB: number): T {
  const shallowCopy = array.slice() as T;
  [shallowCopy[indexA], shallowCopy[indexB]] = [shallowCopy[indexB], shallowCopy[indexA]];
  return shallowCopy;
}

export function isTruthy<T>(x: T | undefined | null): x is T {
  return !!x;
}

export function generateUuid(): string | undefined {
  try {
    const globalObject = getGlobalObject();
    if (!globalObject || !globalObject.crypto) {
      return undefined;
    }

    if (globalObject.crypto.randomUUID) {
      return globalObject.crypto.randomUUID();
    }

    // https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid/2117523#2117523
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (match) =>
      (+match ^ (globalObject.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+match / 4)))).toString(16),
    );
  } catch (e) {
    return undefined;
  }
}
